(function () {
  'use strict';

  var MODE = Object.freeze({
    SELECT_SIZE: 'select-size',
    PREORDER: 'preorder',
    ADD: 'add',
    NOTIFY: 'notify',
  });

  var COPY = Object.freeze({
    selectSize: 'SELECT SIZE',
    preorder: 'PRE-ORDER',
    notify: 'NOTIFY ME',
    submit: 'NOTIFY ME',
    loading: 'SAVING…',
    success: "You're on the list. Check your email to confirm.",
    duplicate: "You're already on the list for this size.",
    alreadyAvailable: 'This size is available now. Refresh to purchase.',
    invalidEmail: 'Enter a valid email address.',
    consent: 'Confirm the alert consent to continue.',
    challenge: 'Complete the security check, then try again.',
    unavailable: 'Restock alerts are unavailable right now.',
    error: "We couldn't save your request. Try again.",
  });

  function normalizeId(value) {
    var match = String(value || '').match(/(\d+)$/);
    return match ? match[1] : '';
  }

  function parseJson(node, fallback) {
    try {
      return JSON.parse(node ? node.textContent : '');
    } catch {
      return fallback;
    }
  }

  function resolveState(input) {
    if (input.requiresSize && !input.sizeSelected) return MODE.SELECT_SIZE;
    if (!input.variantId) return input.requiresSize ? MODE.SELECT_SIZE : MODE.NOTIFY;
    if (input.preorderIds.has(input.variantId)) return MODE.PREORDER;
    return input.available ? MODE.ADD : MODE.NOTIFY;
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254;
  }

  function setButtonLabel(button, label) {
    if (!button) return;
    var labelNode = button.querySelector('span');
    if (labelNode && labelNode.textContent.trim() !== label) labelNode.textContent = label;
    if (!labelNode && button.textContent.trim() !== label) button.textContent = label;
  }

  function setButtonState(button, mode, addLabel, selectSizeEnabled) {
    if (!button) return;
    var disabled = mode === MODE.SELECT_SIZE && !selectSizeEnabled;
    var type = mode === MODE.NOTIFY ? 'button' : 'submit';
    var label = addLabel;
    if (mode === MODE.SELECT_SIZE) label = COPY.selectSize;
    if (mode === MODE.PREORDER) label = COPY.preorder;
    if (mode === MODE.NOTIFY) label = COPY.notify;

    if (button.disabled !== disabled) button.disabled = disabled;
    if (button.getAttribute('type') !== type) button.setAttribute('type', type);
    if (button.getAttribute('aria-disabled') !== String(disabled)) {
      button.setAttribute('aria-disabled', String(disabled));
    }
    setButtonLabel(button, label);
  }

  function loadTurnstile() {
    if (window.turnstile) return window.Promise.resolve(window.turnstile);
    if (window.__zicoTurnstilePromise) return window.__zicoTurnstilePromise;
    window.__zicoTurnstilePromise = new window.Promise(function (resolve, reject) {
      var script = document.querySelector('script[data-zico-turnstile-script]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.zicoTurnstileScript = 'true';
        document.head.appendChild(script);
      }
      script.addEventListener('load', function () {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new window.Error('turnstile_unavailable'));
      }, { once: true });
      script.addEventListener('error', function () {
        reject(new window.Error('turnstile_unavailable'));
      }, { once: true });
    });
    return window.__zicoTurnstilePromise;
  }

  class ZicoCommerceState extends window.HTMLElement {
    connectedCallback() {
      if (this.dataset.zicoBound === 'true') return;
      this.dataset.zicoBound = 'true';

      this.buyBlock = this.closest('[data-zico-buy-block]');
      if (!this.buyBlock) return;

      this.productForm = this.buyBlock.querySelector('product-form');
      this.variantInput = this.buyBlock.querySelector('.product-variant-id');
      this.submitButton = this.buyBlock.querySelector('[id^="ProductSubmitButton-"]');
      this.noteInput = this.querySelector('[data-zico-preorder-note]');
      this.campaignInput = this.querySelector('[data-zico-preorder-campaign]');
      this.preorderMessage = this.querySelector('[data-zico-preorder-message]');
      this.restockForm = this.querySelector('[data-zico-restock-form]');
      this.restockFieldset = this.restockForm && this.restockForm.querySelector('fieldset');
      this.emailInput = this.querySelector('[data-zico-restock-email]');
      this.consentInput = this.querySelector('[data-zico-restock-consent]');
      this.restockSubmit = this.querySelector('[data-zico-restock-submit]');
      this.restockSubmitLabel = this.querySelector('[data-zico-restock-submit-label]');
      this.statusNode = this.querySelector('[data-zico-restock-status]');
      this.turnstileNode = this.querySelector('[data-zico-turnstile]');
      this.endpoint = String(this.dataset.endpoint || '').trim();
      this.turnstileSiteKey = String(this.dataset.turnstileSiteKey || '').trim();
      this.turnstileToken = '';
      this.turnstileWidgetId = null;
      this.syncTimer = null;
      this.followupSyncTimer = null;
      this.currentMode = this.dataset.mode || MODE.ADD;
      this.currentVariantId = '';
      this.restockExpanded = false;
      this.preorderIds = new Set(
        parseJson(this.querySelector('[data-zico-preorder-variant-ids]'), []).map(normalizeId).filter(Boolean),
      );
      this.variants = new Map(
        parseJson(this.buyBlock.querySelector('[data-zico-product-variants]'), []).map(function (variant) {
          return [normalizeId(variant.id), variant];
        }),
      );
      this.addLabel =
        (this.submitButton && this.submitButton.dataset.zicoAddLabel) || 'ADD TO CART';
      this.abortController = new window.AbortController();
      this.lastSubscribedVariantId = '';

      this.bindEvents();
      this.observeThemeChanges();
      this.sync();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.observer) this.observer.disconnect();
      window.clearTimeout(this.syncTimer);
      window.clearTimeout(this.followupSyncTimer);
      this.syncTimer = null;
      this.followupSyncTimer = null;
      this.turnstileToken = '';
      if (this.turnstileWidgetId !== null && this.turnstileWidgetId !== undefined) {
        try {
          if (window.turnstile && typeof window.turnstile.remove === 'function') {
            window.turnstile.remove(this.turnstileWidgetId);
          }
        } catch {
          // The element is already detached; clearing local state is sufficient.
        }
        this.turnstileWidgetId = null;
      }
      this.dataset.zicoBound = 'false';
    }

    bindEvents() {
      var signal = this.abortController.signal;
      var schedule = this.scheduleSync.bind(this);
      document.addEventListener('change', schedule, { capture: true, signal: signal });
      document.addEventListener('variant:change', schedule, { signal: signal });
      document.addEventListener('variantChange', schedule, { signal: signal });
      document.addEventListener('click', this.handleDocumentClick.bind(this), {
        capture: true,
        signal: signal,
      });
      if (this.restockForm) {
        this.restockForm.addEventListener('submit', this.handleRestockSubmit.bind(this), { signal: signal });
      }
    }

    observeThemeChanges() {
      if (!window.MutationObserver) return;
      this.observer = new window.MutationObserver(this.scheduleSync.bind(this));
      if (this.productForm) {
        this.observer.observe(this.productForm, {
          attributes: true,
          attributeFilter: ['data-zico-size-selected'],
        });
      }
      if (this.variantInput) {
        this.observer.observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value', 'disabled'],
        });
      }
      if (this.submitButton) {
        this.observer.observe(this.submitButton, { childList: true, subtree: true });
      }
      var stickyButton = document.querySelector('[data-zico-sticky-submit]');
      if (stickyButton) {
        this.observer.observe(stickyButton, {
          attributes: true,
          attributeFilter: ['disabled', 'type'],
          childList: true,
          subtree: true,
        });
      }
    }

    scheduleSync() {
      if (!this.isConnected) return;
      window.clearTimeout(this.syncTimer);
      window.clearTimeout(this.followupSyncTimer);
      this.syncTimer = window.setTimeout(() => {
        this.syncTimer = null;
        if (this.isConnected) this.sync();
      }, 40);
      this.followupSyncTimer = window.setTimeout(() => {
        this.followupSyncTimer = null;
        if (this.isConnected) this.sync();
      }, 140);
    }

    selectedVariant() {
      var variantId = normalizeId(this.variantInput && this.variantInput.value);
      return { id: variantId, record: this.variants.get(variantId) || null };
    }

    sync() {
      var selected = this.selectedVariant();
      var requiresSize = Boolean(this.productForm && this.productForm.dataset.zicoRequiresSize === 'true');
      var sizeSelected = !requiresSize || Boolean(this.productForm && this.productForm.dataset.zicoSizeSelected === 'true');
      var available = Boolean(
        selected.record && selected.record.available && !selected.record.quantity_rule_soldout,
      );
      var mode = resolveState({
        requiresSize: requiresSize,
        sizeSelected: sizeSelected,
        variantId: selected.id,
        preorderIds: this.preorderIds,
        available: available,
      });
      var variantChanged = selected.id !== this.currentVariantId;

      this.currentMode = mode;
      this.currentVariantId = selected.id;
      this.dataset.mode = mode;

      if (this.variantInput) {
        var disableVariant = mode === MODE.SELECT_SIZE || mode === MODE.NOTIFY;
        if (this.variantInput.disabled !== disableVariant) this.variantInput.disabled = disableVariant;
      }
      if (this.noteInput) {
        this.noteInput.disabled = mode !== MODE.PREORDER;
        this.noteInput.value = mode === MODE.PREORDER ? 'Preorder' : '';
      }
      if (this.campaignInput) {
        this.campaignInput.disabled = mode !== MODE.PREORDER;
        this.campaignInput.value = mode === MODE.PREORDER ? '2-3-week-20260828' : '';
      }
      if (variantChanged) this.resetRestockForm();
      if (this.preorderMessage) this.preorderMessage.hidden = mode !== MODE.PREORDER;
      if (this.restockForm) this.restockForm.hidden = mode !== MODE.NOTIFY || !this.restockExpanded;

      setButtonState(this.submitButton, mode, this.addLabel, false);
      this.syncRestockDisclosure(this.submitButton, mode);
      this.syncSticky(mode);

      this.dispatchEvent(
        new window.CustomEvent('zico:commerce-state', {
          bubbles: true,
          detail: { mode: mode, variantId: selected.id },
        }),
      );
    }

    syncSticky(mode) {
      var sticky = document.querySelector('[data-zico-sticky-atc]');
      if (!sticky) return;
      sticky.hidden = mode === MODE.NOTIFY && this.restockExpanded;
      var button = sticky.querySelector('[data-zico-sticky-submit]');
      var input = sticky.querySelector('.product-variant-id');
      if (!button) return;

      if (!button.dataset.zicoAddLabel) {
        button.dataset.zicoAddLabel = this.addLabel;
      }
      if (input && this.currentVariantId) input.value = this.currentVariantId;
      setButtonState(button, mode, button.dataset.zicoAddLabel || this.addLabel, true);
      this.syncRestockDisclosure(button, mode);
      button.dataset.zicoCommerceMode = mode;
    }

    syncRestockDisclosure(button, mode) {
      if (!button) return;
      if (mode === MODE.NOTIFY && this.restockForm) {
        button.setAttribute('aria-controls', this.restockForm.id);
        button.setAttribute('aria-expanded', String(this.restockExpanded));
        return;
      }
      button.removeAttribute('aria-controls');
      button.removeAttribute('aria-expanded');
    }

    expandRestockForm() {
      if (!this.restockForm) return;
      var wasExpanded = this.restockExpanded;
      this.restockExpanded = true;
      this.restockForm.dataset.expanded = 'true';
      this.restockForm.hidden = false;
      this.syncRestockDisclosure(this.submitButton, this.currentMode);
      var sticky = document.querySelector('[data-zico-sticky-atc]');
      if (sticky) sticky.hidden = true;
      var stickyButton = sticky && sticky.querySelector('[data-zico-sticky-submit]');
      this.syncRestockDisclosure(stickyButton, this.currentMode);
      if (!wasExpanded) {
        if (this.turnstileWidgetId === null) this.initializeChallenge();
        else this.resetChallenge();
      }
      var terminalState = ['success', 'duplicate', 'available'].includes(this.restockForm.dataset.state || '');
      window.setTimeout(() => {
        if (terminalState && this.statusNode) this.statusNode.focus({ preventScroll: true });
        else if (this.emailInput && !this.emailInput.disabled) this.emailInput.focus({ preventScroll: true });
        else if (this.statusNode) this.statusNode.focus({ preventScroll: true });
      }, 0);
    }

    handleDocumentClick(event) {
      var button = event.target && event.target.closest
        ? event.target.closest('[id^="ProductSubmitButton-"], [data-zico-sticky-submit]')
        : null;
      if (!button) return;
      var stickyButton = button.matches('[data-zico-sticky-submit]');
      if (this.currentMode === MODE.PREORDER && stickyButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitButton) this.submitButton.click();
        return;
      }
      if (this.currentMode !== MODE.NOTIFY) return;
      if (button !== this.submitButton && !stickyButton) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.expandRestockForm();
    }

    resetRestockForm() {
      if (!this.restockForm) return;
      this.restockExpanded = false;
      this.restockForm.dataset.expanded = 'false';
      this.restockForm.hidden = true;
      this.restockForm.dataset.state = 'idle';
      if (this.emailInput) this.emailInput.value = '';
      if (this.consentInput) this.consentInput.checked = false;
      if (this.restockSubmit) this.restockSubmit.disabled = !this.endpoint || !this.turnstileSiteKey;
      if (this.restockSubmitLabel) this.restockSubmitLabel.textContent = COPY.submit;
      this.resetChallenge();
      this.setStatus(this.endpoint && this.turnstileSiteKey ? '' : COPY.unavailable, false);
    }

    async initializeChallenge() {
      if (!this.endpoint || !this.turnstileSiteKey || !this.turnstileNode) return;
      try {
        var turnstile = await loadTurnstile();
        if (!this.isConnected || this.turnstileWidgetId !== null) return;
        this.turnstileWidgetId = turnstile.render(this.turnstileNode, {
          sitekey: this.turnstileSiteKey,
          action: 'restock_subscribe',
          appearance: 'interaction-only',
          size: 'flexible',
          theme: 'light',
          language: 'en',
          callback: (token) => {
            this.turnstileToken = String(token || '');
          },
          'expired-callback': () => {
            this.turnstileToken = '';
          },
          'error-callback': () => {
            this.turnstileToken = '';
          },
        });
      } catch {
        this.turnstileToken = '';
        this.setStatus(COPY.challenge, true);
      }
    }

    resetChallenge() {
      this.turnstileToken = '';
      if (this.turnstileWidgetId !== null && window.turnstile) {
        try {
          window.turnstile.reset(this.turnstileWidgetId);
        } catch {
          this.turnstileWidgetId = null;
          this.initializeChallenge();
        }
      }
    }

    setStatus(message, assertive) {
      if (!this.statusNode) return;
      this.statusNode.textContent = message;
      this.statusNode.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
      if (message) this.statusNode.setAttribute('tabindex', '-1');
      else this.statusNode.removeAttribute('tabindex');
    }

    async handleRestockSubmit(event) {
      event.preventDefault();
      this.sync();
      if (this.currentMode !== MODE.NOTIFY) return;
      if (!this.endpoint || !this.turnstileSiteKey) {
        this.setStatus(COPY.unavailable, false);
        return;
      }

      var email = String((this.emailInput && this.emailInput.value) || '').trim().toLowerCase();
      if (!validEmail(email)) {
        this.restockForm.dataset.state = 'error';
        this.setStatus(COPY.invalidEmail, true);
        if (this.emailInput) this.emailInput.focus();
        return;
      }
      if (!this.consentInput || !this.consentInput.checked) {
        this.restockForm.dataset.state = 'error';
        this.setStatus(COPY.consent, true);
        if (this.consentInput) this.consentInput.focus();
        return;
      }
      if (!this.currentVariantId) {
        this.setStatus(COPY.error, true);
        return;
      }
      if (!this.turnstileToken) {
        this.restockForm.dataset.state = 'error';
        this.setStatus(COPY.challenge, true);
        return;
      }

      this.restockForm.dataset.state = 'loading';
      if (this.restockSubmit) this.restockSubmit.disabled = true;
      if (this.restockSubmitLabel) this.restockSubmitLabel.textContent = COPY.loading;
      this.setStatus(COPY.loading, false);

      try {
        var response = await window.fetch(this.endpoint, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            productId: 'gid://shopify/Product/' + normalizeId(this.dataset.productId),
            productHandle: String(this.dataset.productHandle || ''),
            variantId: 'gid://shopify/ProductVariant/' + this.currentVariantId,
            consent: true,
            turnstileToken: this.turnstileToken,
          }),
        });
        var result = {};
        try {
          result = await response.json();
        } catch {
          result = {};
        }

        if (result.status === 'already_available') {
          this.restockForm.dataset.state = 'available';
          this.setStatus(COPY.alreadyAvailable, false);
          return;
        }
        if (response.status === 409 || result.status === 'duplicate') {
          this.restockForm.dataset.state = 'duplicate';
          this.setStatus(COPY.duplicate, false);
          return;
        }
        if (!response.ok) throw new window.Error('subscription_failed');

        this.lastSubscribedVariantId = this.currentVariantId;
        this.restockForm.dataset.state = 'success';
        this.setStatus(COPY.success, false);
      } catch {
        this.restockForm.dataset.state = 'error';
        this.setStatus(COPY.error, true);
        if (this.restockSubmit) this.restockSubmit.disabled = false;
      } finally {
        this.resetChallenge();
        if (this.restockSubmitLabel) this.restockSubmitLabel.textContent = COPY.submit;
      }
    }
  }

  if (!window.customElements.get('zico-commerce-state')) {
    window.customElements.define('zico-commerce-state', ZicoCommerceState);
  }

  window.ZicoPreorderBackInStock = Object.freeze({ MODE: MODE, resolveState: resolveState, validEmail: validEmail });
})();
